import { trackCall } from "@/lib/track";
import React, { useEffect, useState, Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowRight, Stethoscope, Disc3, Cog, CircleDot, Droplets, Snowflake, ShieldCheck, BookOpen, Star, ChevronRight, Wrench, ShieldCheck as ShieldIcon, Gauge, Award } from "lucide-react";
import { ScrollGear, ScrollScrew } from "@/components/ScrollGear";
import { GoogleReviews } from "@/components/GoogleReviews";
import { HighlightsWall } from "@/components/HighlightsWall";
import { vehiclePhotoUrl, formatPrice } from "@/pages/VehicleListPage";
import { HeroCallouts } from "@/components/twin/HeroCallouts";
import { CommandCenter } from "@/components/twin/CommandCenter";
import { RepcoClientPanel } from "@/components/twin/RepcoClientPanel";
import { ReviewTicker } from "@/components/twin/ReviewTicker";

const EngineTwin3D = lazy(() => import("@/components/twin/EngineTwin3D"));

const ICONS = { Stethoscope, Disc3, Cog, CircleDot, Droplets, Snowflake, ShieldCheck, BookOpen };

const INTERIOR_IMG = "https://customer-assets.emergentagent.com/job_garage-modern-2/artifacts/03uy0lxw_IMG_8278.png";
const BRAKE_IMG = "https://customer-assets.emergentagent.com/job_garage-modern-2/artifacts/q4tbz2bf_IMG_8276.png";

const FAQS = [
  {
    q: "Where is Tassie Automotive located?",
    a: "You'll find us at 46 Grove Road, Glenorchy TAS 7010 — five minutes from Glenorchy's main shopping strip and an easy drive from Moonah, Claremont, Montrose and greater Hobart. Parking is available on site and you're welcome to wait or drop your keys and go.",
  },
  {
    q: "Do you service all makes and models?",
    a: "Yes. As a Repco Authorised workshop we service and repair all popular makes — Toyota, Ford, Mazda, Holden, Subaru, Hyundai, Kia, Mitsubishi, Volkswagen, BMW, Mercedes-Benz and more — from small hatchbacks to 4WDs, utes and light commercial vehicles.",
  },
  {
    q: "Will servicing with you keep my new-car warranty?",
    a: "Absolutely. Logbook servicing carried out by a qualified workshop like ours, using quality parts and fluids to the manufacturer's schedule, fully preserves your new-car warranty. We stamp your logbook every visit.",
  },
  {
    q: "How do I book, and do I need an account?",
    a: "Book online in about 60 seconds — pick a service, choose a day and time that suits, and you're done. No account needed. You'll get an email and SMS straight away, a confirmation once we lock it in, and a reminder the day before.",
  },
  {
    q: "What are your opening hours?",
    a: "We're open Monday to Friday 8am–5pm, Saturday 9am–5pm and Sunday 10am–5pm. If a time slot is full online, give us a call on (03) 6288 7861 and we'll do our best to fit you in.",
  },
  {
    q: "Do you sell or rent cars too?",
    a: "Yes — we keep a small range of workshop-checked used cars for sale and well-maintained rental vehicles. Every car we sell has been through our own workshop, so you know exactly what you're buying. Browse current stock on our Cars for Sale and Rentals pages.",
  },
];

const Marquee = () => {
  const items = ["Toyota", "Ford", "Mazda", "Holden", "Mitsubishi", "Subaru", "Volkswagen", "Hyundai", "Kia", "BMW", "Mercedes-Benz", "Audi", "Nissan", "Isuzu", "Land Rover"];
  return (
    <div className="overflow-hidden border-y border-zinc-900 bg-zinc-950/40 py-6">
      <div className="marquee-track flex gap-12 whitespace-nowrap">
        {[...items, ...items].map((b, i) => (
          <span key={`${b}-${i}`} className="font-display uppercase text-3xl tracking-tight text-zinc-700 hover:text-brand-red transition-colors">
            {b} <span className="text-brand-red">/</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/vehicles/featured").then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page" className="bg-background text-foreground relative">
      {/* Decorative scroll-driven gears (behind content) */}
      <div className="absolute inset-0 pointer-events-none z-0 hidden lg:block">
        <ScrollGear
          variant="tyre"
          size={380}
          speed={0.25}
          className="absolute top-[120vh] -right-32 opacity-25"
        />
        <ScrollGear
          variant="gear"
          size={240}
          speed={-0.4}
          className="absolute top-[200vh] -left-20 opacity-20"
        />
        <ScrollScrew
          size={90}
          speed={0.8}
          className="absolute top-[60vh] right-10 opacity-50"
        />
        <ScrollScrew
          size={70}
          speed={-1.1}
          className="absolute top-[150vh] left-8 opacity-50"
        />
        <ScrollGear
          variant="tyre"
          size={300}
          speed={0.35}
          className="absolute top-[260vh] -right-24 opacity-25"
        />
      </div>
      <div className="relative z-10">

      {/* DIGITAL TWIN HERO */}
      <section data-testid="digital-twin-hero" className="relative hud-grid-bg overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 pt-6 pb-10 grid grid-cols-1 lg:grid-cols-12 gap-5 items-stretch">
          <div className="lg:col-span-8 flex flex-col gap-5">
            <div className="relative hud-panel rounded-xl overflow-hidden flex-1">
              <div className="absolute top-3 left-4 z-20 flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-red blink-dot" />
                <span data-testid="hero-eyebrow" className="font-mono text-[9px] uppercase tracking-[0.35em] text-zinc-400">Digital twin · V8-TT.04 · drag to rotate</span>
              </div>
              <div className="relative h-[360px] sm:h-[460px]">
                <Suspense
                  fallback={
                    <div className="absolute inset-0 grid place-items-center">
                      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-red animate-pulse">Initialising digital twin…</span>
                    </div>
                  }
                >
                  <EngineTwin3D />
                </Suspense>
                <HeroCallouts />
              </div>

              {/* Red title banner + key services */}
              <div className="relative grid grid-cols-1 md:grid-cols-3 border-t border-brand-red/25">
                <div className="md:col-span-2 banner-clip bg-gradient-to-r from-brand-red/25 via-black to-black p-6 sm:p-8">
                  <h1 data-testid="hero-title" className="font-display uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.9] tracking-tighter">
                    The Performance <span className="text-brand-red">Edge:</span>
                  </h1>
                  <p data-testid="hero-subtitle" className="mt-3 font-tech uppercase text-base md:text-lg text-zinc-300 tracking-wide max-w-xl">
                    Your vehicle, optimized. Precision tuning &amp; diagnostics for modern &amp; classic cars.
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row gap-3">
                    <Link data-testid="hero-book-btn" to="/book" className="glow-btn inline-flex items-center justify-center gap-2 px-7 py-3.5 bg-brand-red text-white font-bold uppercase tracking-wider rounded hover:bg-[#B91C2B] transition-colors">
                      Book a service <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link data-testid="hero-services-btn" to="/services" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 border border-brand-red/50 text-white font-bold uppercase tracking-wider rounded hover:bg-brand-red/10 transition-colors">
                      Explore tech lab
                    </Link>
                  </div>
                </div>
                <div className="p-6 sm:p-8 border-t md:border-t-0 md:border-l border-brand-red/20 bg-black/50">
                  <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-red mb-4">Key services:</div>
                  <div className="space-y-3">
                    {[
                      { Icon: Stethoscope, t: "Diagnostics" },
                      { Icon: BookOpen, t: "Logbook" },
                      { Icon: Wrench, t: "Repairs" },
                    ].map(({ Icon, t }) => (
                      <div key={t} className="flex items-center gap-3">
                        <span className="h-9 w-9 grid place-items-center border border-brand-red/40 bg-brand-red/10 rounded">
                          <Icon className="h-4 w-4 text-brand-red" />
                        </span>
                        <span className="font-tech uppercase text-sm text-white tracking-wider">{t}</span>
                      </div>
                    ))}
                  </div>
                  <img src="/repco-badge.png" alt="Repco Authorised Service" className="mt-5 w-32 opacity-90" />
                </div>
              </div>
            </div>

            {/* Stat strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900 rounded-xl overflow-hidden">
              {[
                { k: "4+", v: "Years on the floor" },
                { k: "Repco", v: "Authorised service" },
                { k: "5★", v: "Customer rated" },
                { k: "100%", v: "Warranty safe" },
              ].map((s) => (
                <div key={s.k} className="bg-black p-5">
                  <div className="font-display text-3xl text-brand-red">{s.k}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <CommandCenter />
          </div>
        </div>
      </section>

      <ReviewTicker />

      <Marquee />

      {/* GOOGLE REVIEWS — moved up so customers see real reviews first */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex items-center gap-3 mb-3">
          <span className="h-px w-12 bg-brand-red" />
          <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">01 — Customer log</span>
        </div>
        <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter text-white max-w-3xl">
          Tasmanians don&apos;t write reviews. <span className="text-brand-red">Until now.</span>
        </h2>

        <div className="mt-12">
          <GoogleReviews testimonials={testimonials} />
        </div>
      </section>

      {/* FEATURED VEHICLES */}
      {featured.length > 0 && (
        <section data-testid="home-featured" className="relative border-b border-zinc-900 bg-gradient-to-b from-zinc-950/60 to-black">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <span className="h-px w-12 bg-brand-red" />
                  <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">Featured stock</span>
                </div>
                <h2 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter text-white leading-none">
                  Fresh on the floor.
                </h2>
              </div>
              <div className="flex gap-3">
                <Link to="/cars-for-sale" data-testid="home-featured-sale-link" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red text-[11px] font-mono uppercase tracking-widest">
                  Cars for sale <ChevronRight className="h-3 w-3" />
                </Link>
                <Link to="/cars-for-rent" data-testid="home-featured-rent-link" className="inline-flex items-center gap-2 px-4 py-2.5 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red text-[11px] font-mono uppercase tracking-widest">
                  Cars for rent <ChevronRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((v) => (
                <Link key={v.id} to={`/vehicles/${v.id}`} data-testid={`home-featured-${v.id}`} className="group block border border-zinc-900 bg-zinc-950 hover:border-brand-red transition-colors overflow-hidden">
                  <div className="aspect-[16/10] bg-zinc-900 overflow-hidden relative">
                    {v.cover_photo ? (
                      <img src={vehiclePhotoUrl(v.cover_photo)} alt={v.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-zinc-700"><Star className="h-8 w-8" /></div>
                    )}
                    <span className={`absolute top-2 left-2 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest ${v.status === "rent" ? "border-blue-500 text-blue-300 bg-blue-950/50" : "border-brand-red text-brand-red bg-brand-red/10"}`}>
                      {v.status === "rent" ? "For rent" : "For sale"}
                    </span>
                  </div>
                  <div className="p-4">
                    <div className="font-display uppercase text-sm text-white tracking-tight line-clamp-2">{v.title}</div>
                    <div className="mt-2 font-display text-xl text-brand-red">
                      {formatPrice(v.price)}{v.status === "rent" && <span className="text-zinc-500 text-xs">/day</span>}
                    </div>
                    <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 group-hover:text-brand-red flex items-center gap-1.5">
                      View details <ArrowRight className="h-3 w-3" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SERVICES BENTO */}
      <section id="services" className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">02 — Services</span>
            </div>
            <h2 data-testid="services-title" className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter text-white max-w-3xl">
              The full mechanical stack — under one roof.
            </h2>
          </div>
          <Link to="/services" data-testid="services-view-all" className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-mono text-brand-red hover:text-white">
            View all services <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
          {services.map((s, i) => {
            const Icon = ICONS[s.icon] || Cog;
            return (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="group bg-zinc-950 p-7 hover:bg-zinc-900/80 transition-colors relative"
              >
                <div className="flex items-start justify-between">
                  <div className="h-12 w-12 border border-zinc-800 grid place-items-center group-hover:border-brand-red transition-colors">
                    <Icon className="h-5 w-5 text-brand-red" strokeWidth={1.8} />
                  </div>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">/0{i + 1}</span>
                </div>
                <h3 className="mt-6 font-display uppercase text-2xl text-white tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.description}</p>
                <div className="mt-6 flex items-center justify-between border-t border-zinc-900 pt-4">
                  <span className="font-mono text-xs text-zinc-500 uppercase tracking-widest">From</span>
                  <span className="font-display text-2xl text-brand-red">${s.price_from}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <RepcoClientPanel />

      {/* PHOTO HIGHLIGHTS WALL */}
      <HighlightsWall limit={3} className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-zinc-900" />

      {/* ABOUT */}
      <section className="relative bg-zinc-950 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="relative">
              <img loading="lazy" decoding="async" src={INTERIOR_IMG} alt="Inside the Tassie Automotive workshop — multi-bay layout with hoists ready" className="w-full h-[560px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-black/80 backdrop-blur-md border border-white/10 p-5">
                <div className="font-mono text-[10px] uppercase tracking-widest text-brand-red">Bay 03 · Hydraulic Lift</div>
                <div className="mt-1 font-display uppercase text-xl text-white tracking-tight">Modern equipment. Old-school care.</div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="flex items-center gap-3 mb-3">
              <span className="h-px w-12 bg-brand-red" />
              <span className="font-mono text-xs uppercase tracking-[0.4em] text-brand-red">03 — The workshop</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tighter text-white">
              Built on torque,<br /><span className="text-brand-red">backed by trust.</span>
            </h2>
            <p className="mt-6 text-zinc-300 leading-relaxed max-w-xl">
              We opened our doors in Glenorchy in 2021 and quickly became one of the region&apos;s most trusted
              workshops — earning Repco Authorised Service accreditation along the way. Today we service everything
              from daily drivers to classic restorations with the same precision, honesty and care.
            </p>

            <div className="mt-10 grid grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { Icon: ShieldIcon, t: "Repco Authorised", d: "Backed by Australia's largest aftermarket network." },
                { Icon: Gauge, t: "Modern & classic", d: "From new daily drivers to vintage restorations." },
                { Icon: Award, t: "Logbook stamped", d: "Manufacturer-spec service, warranty intact." },
                { Icon: Wrench, t: "Electrical works", d: "Diagnostics, wiring, ECU and battery service." },
              ].map((f) => (
                <div key={f.t} className="bg-black p-6">
                  <f.Icon className="h-5 w-5 text-brand-red" />
                  <div className="mt-3 font-display uppercase text-base tracking-tight text-white">{f.t}</div>
                  <p className="text-xs text-zinc-500 mt-1 leading-relaxed">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section data-testid="home-faq" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 border-t border-zinc-900">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">Good to know</p>
        <h2 className="mt-3 font-display uppercase text-3xl sm:text-4xl tracking-tight text-white">
          Common questions, straight answers
        </h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-10">
          {FAQS.map((f) => (
            <div key={f.q}>
              <h3 className="text-white font-semibold">{f.q}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            // Static FAQ constants only; escape "<" as defence-in-depth against script breakout
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: FAQS.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }).replace(/</g, "\\u003c"),
          }}
        />
      </section>

      <section className="relative noise-overlay overflow-hidden border-y border-zinc-900">
        <img src={BRAKE_IMG} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter text-white">
              Stop guessing.<br />
              <span className="text-brand-red">Start driving.</span>
            </h2>
            <p className="mt-6 max-w-xl text-zinc-300">
              Book in 60 seconds. Pick a service, choose your day, drop your keys. We&apos;ll handle the rest —
              and you can track every step from your phone.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3">
            <Link data-testid="cta-book-btn" to="/book" className="inline-flex items-center justify-between px-8 py-5 bg-brand-red text-white font-display uppercase text-xl tracking-tight hover:bg-[#B91C2B] transition-colors">
              Book your service <ArrowRight className="h-5 w-5" />
            </Link>
            <a data-testid="cta-call-btn" href="tel:+61362887861" onClick={() => trackCall("home_cta")} className="inline-flex items-center justify-between px-8 py-5 border border-white/20 text-white font-display uppercase text-xl tracking-tight hover:border-brand-red hover:text-brand-red transition-colors">
              Call (03) 6288 7861 <ChevronRight className="h-5 w-5" />
            </a>
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
